/**
 * Centralised MIME type constants used across the monorepo.
 *
 * Naming convention:
 *  - `*_MIME_TYPE` (singular) → one specific MIME type literal.
 *  - `*_MIME_TYPES` (plural)  → a `readonly` tuple grouping related types.
 *  - `is*File` helpers        → narrow a `string` mimeType to a known group.
 *
 * When adding a new MIME type, prefer extending an existing group rather than
 * scattering string literals across the codebase.
 */

import type { ToolMimeType } from "./brain/retriever";

// ──────────────────────────────────────────────────────────────────────────────
// Documents
// ──────────────────────────────────────────────────────────────────────────────

export const PDF_MIME_TYPE = "application/pdf";
export const SOPHOS_PDF_MIME_TYPE = "Adobe Portable Document Format (PDF)";

// MS Word
export const WORD_DOC_MIME_TYPE = "application/msword";
export const SOPHOS_WORD_DOC_MIME_TYPE = "Microsoft Word 95 to 2003";
export const WORD_DOCX_MIME_TYPE =
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const SOPHOS_WORD_DOCX_MIME_TYPE = "Microsoft Word 2007 onwards";

// MS Excel
export const EXCEL_XLS_MIME_TYPE = "application/vnd.ms-excel";
export const SOPHOS_EXCEL_XLS_MIME_TYPE = "Microsoft Excel 95 to 2003";
export const EXCEL_XLSX_MIME_TYPE =
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const SOPHOS_EXCEL_XLSX_MIME_TYPE = "Microsoft Excel 2007 onwards";

// MS PowerPoint
export const POWERPOINT_PPT_MIME_TYPE = "application/vnd.ms-powerpoint";
export const SOPHOS_POWERPOINT_PPT_MIME_TYPE = "Microsoft PowerPoint 95 to 2003";
export const POWERPOINT_PPTX_MIME_TYPE =
	"application/vnd.openxmlformats-officedocument.presentationml.presentation";
export const SOPHOS_POWERPOINT_PPTX_MIME_TYPE_1 = "Microsoft PowerPoint 2007 onwards";
export const SOPHOS_POWERPOINT_PPTX_MIME_TYPE_2 = "PK ZIP archive";

// MS Outlook
export const OUTLOOK_MSG_MIME_TYPE = "application/vnd.ms-outlook";
export const SOPHOS_OUTLOOK_MSG_MIME_TYPE = "Microsoft Outlook 97 to 2003";

// MS Compound File Binary — legacy Office wrapper (.doc/.xls/.ppt/.msg containers).
export const CFB_MIME_TYPE = "application/x-cfb";

// ──────────────────────────────────────────────────────────────────────────────
// Text
// ──────────────────────────────────────────────────────────────────────────────

export const TEXT_PLAIN_MIME_TYPE = "text/plain";
export const SOPHOS_TEXT_PLAIN_MIME_TYPE_1 = "ASCII text / UTF-8 file";
export const SOPHOS_TEXT_PLAIN_MIME_TYPE_2 = "UTF-16/UCS-2 Unicode text";
export const SOPHOS_TEXT_PLAIN_MIME_TYPE_3 = "UTF-32/UCS-4 Unicode text";
export const SOPHOS_TEXT_PLAIN_MIME_TYPE_4 = "Windows Codepage 1252";
export const SOPHOS_TEXT_PLAIN_MIME_TYPE_5 = "ASCII text / 8-bit Unicode Transformation Format";

export const TEXT_HTML_MIME_TYPE = "text/html";
export const SOPHOS_TEXT_HTML_MIME_TYPE = "Hypertext Markup Language";
export const TEXT_MARKDOWN_MIME_TYPE = "text/markdown";
export const SOPHOS_TEXT_MARKDOWN_MIME_TYPE = "";
export const TEXT_CSV_MIME_TYPE = "text/csv";
export const SOPHOS_TEXT_CSV_MIME_TYPE = "Comma-separated values";

// ──────────────────────────────────────────────────────────────────────────────
// Images
// ──────────────────────────────────────────────────────────────────────────────

export const IMAGE_PNG_MIME_TYPE = "image/png";
export const SOPHOS_IMAGE_PNG_MIME_TYPE = "PNG (Portable Network Graphics) image format";
export const IMAGE_JPEG_MIME_TYPE = "image/jpeg";
export const SOPHOS_IMAGE_JPEG_MIME_TYPE_1 = "JPEG Interchange Format";
export const SOPHOS_IMAGE_JPEG_MIME_TYPE_2 = "JPEG image file with Exif metadata";
export const SOPHOS_IMAGE_JPEG_MIME_TYPE_3 = "Exchangeable image file format";

export const IMAGE_WEBP_MIME_TYPE = "image/webp";
export const SOPHOS_IMAGE_WEBP_MIME_TYPE = "Resource Interchange File Format (RIFF)";
export const IMAGE_GIF_MIME_TYPE = "image/gif";
export const IMAGE_SVG_XML_MIME_TYPE = "image/svg+xml";

// ──────────────────────────────────────────────────────────────────────────────
// Audio / Video
// ──────────────────────────────────────────────────────────────────────────────

export const AUDIO_MPEG_MIME_TYPE = "audio/mpeg";
export const SOPHOS_AUDIO_MPEG_MIME_TYPE = "MPEG Audio Stream";
export const AUDIO_MP3_MIME_TYPE = "audio/mp3";
export const SOPHOS_AUDIO_MP3_MIME_TYPE = "ID3 audio file data tagging format";
export const AUDIO_MP4_MIME_TYPE = "audio/mp4";
export const SOPHOS_AUDIO_MP4_MIME_TYPE_1 = "3GP Video for mobile phones";
export const SOPHOS_AUDIO_MP4_MIME_TYPE_2 = "MP4 Base Media v1 / v2";
export const AUDIO_M4A_MIME_TYPE = "audio/x-m4a";
export const SOPHOS_AUDIO_M4A_MIME_TYPE = "Apple iTunes AAC-LC Audio";

export const VIDEO_MP4_MIME_TYPE = "video/mp4";
export const SOPHOS_VIDEO_MP4_MIME_TYPE = "MPEG-4 audio-visual media";
export const VIDEO_3GPP_MIME_TYPE = "video/3gpp";

// ──────────────────────────────────────────────────────────────────────────────
// Misc
// ──────────────────────────────────────────────────────────────────────────────

export const ZIP_MIME_TYPE = "application/zip";
export const JSON_MIME_TYPE = "application/json";
export const OCTET_STREAM_MIME_TYPE = "application/octet-stream";
export const GOOGLE_DRIVE_FOLDER_MIME_TYPE = "application/vnd.google-apps.folder";
export const GOOGLE_DRIVE_SHORTCUT_MIME_TYPE = "application/vnd.google-apps.shortcut";

// ──────────────────────────────────────────────────────────────────────────────
// Grouped tuples
// ──────────────────────────────────────────────────────────────────────────────

export const WORD_MIME_TYPES = [WORD_DOCX_MIME_TYPE, WORD_DOC_MIME_TYPE] as const;

export const EXCEL_MIME_TYPES = [EXCEL_XLSX_MIME_TYPE, EXCEL_XLS_MIME_TYPE] as const;

export const SHEET_MIME_TYPE = [...EXCEL_MIME_TYPES, TEXT_CSV_MIME_TYPE] as const;

export const POWERPOINT_MIME_TYPES = [POWERPOINT_PPTX_MIME_TYPE, POWERPOINT_PPT_MIME_TYPE] as const;

export const OFFICE_MIME_TYPES = [
	...POWERPOINT_MIME_TYPES,
	...WORD_MIME_TYPES,
	...EXCEL_MIME_TYPES,
] as const;

export const TEXT_MIME_TYPES = [
	TEXT_PLAIN_MIME_TYPE,
	TEXT_HTML_MIME_TYPE,
	TEXT_MARKDOWN_MIME_TYPE,
	TEXT_CSV_MIME_TYPE,
] as const;

export const IMAGE_MIME_TYPES = [
	IMAGE_PNG_MIME_TYPE,
	IMAGE_JPEG_MIME_TYPE,
	IMAGE_WEBP_MIME_TYPE,
] as const;

export const AUDIO_MIME_TYPES = [
	AUDIO_MPEG_MIME_TYPE,
	AUDIO_MP3_MIME_TYPE,
	AUDIO_MP4_MIME_TYPE,
	AUDIO_M4A_MIME_TYPE,
] as const;

export const VIDEO_MIME_TYPES = [VIDEO_MP4_MIME_TYPE, VIDEO_3GPP_MIME_TYPE] as const;

/**
 * Every MIME type accepted as a `Content` in the platform.
 */
export const CONTENT_MIME_TYPES = [
	TEXT_HTML_MIME_TYPE,
	TEXT_PLAIN_MIME_TYPE,
	TEXT_MARKDOWN_MIME_TYPE,
	TEXT_CSV_MIME_TYPE,
	PDF_MIME_TYPE,
	WORD_DOC_MIME_TYPE,
	OUTLOOK_MSG_MIME_TYPE,
	WORD_DOCX_MIME_TYPE,
	EXCEL_XLS_MIME_TYPE,
	EXCEL_XLSX_MIME_TYPE,
	POWERPOINT_PPT_MIME_TYPE,
	POWERPOINT_PPTX_MIME_TYPE,
	AUDIO_MPEG_MIME_TYPE,
	AUDIO_MP3_MIME_TYPE,
	AUDIO_MP4_MIME_TYPE,
	VIDEO_MP4_MIME_TYPE,
	AUDIO_M4A_MIME_TYPE,
	IMAGE_PNG_MIME_TYPE,
	IMAGE_JPEG_MIME_TYPE,
	IMAGE_WEBP_MIME_TYPE,
] as const;

export type ContentMimeType = (typeof CONTENT_MIME_TYPES)[number];

export const SOPHOS_CONTENT_MIME_TYPES = [
	SOPHOS_PDF_MIME_TYPE,
	SOPHOS_WORD_DOC_MIME_TYPE,
	SOPHOS_WORD_DOCX_MIME_TYPE,
	SOPHOS_EXCEL_XLS_MIME_TYPE,
	SOPHOS_EXCEL_XLSX_MIME_TYPE,
	SOPHOS_POWERPOINT_PPT_MIME_TYPE,
	SOPHOS_POWERPOINT_PPTX_MIME_TYPE_1,
	SOPHOS_POWERPOINT_PPTX_MIME_TYPE_2,
	SOPHOS_OUTLOOK_MSG_MIME_TYPE,
	SOPHOS_TEXT_PLAIN_MIME_TYPE_1,
	SOPHOS_TEXT_PLAIN_MIME_TYPE_2,
	SOPHOS_TEXT_PLAIN_MIME_TYPE_3,
	SOPHOS_TEXT_PLAIN_MIME_TYPE_4,
	SOPHOS_TEXT_PLAIN_MIME_TYPE_5,
	SOPHOS_TEXT_HTML_MIME_TYPE,
	SOPHOS_TEXT_MARKDOWN_MIME_TYPE,
	SOPHOS_TEXT_CSV_MIME_TYPE,
	SOPHOS_IMAGE_PNG_MIME_TYPE,
	SOPHOS_IMAGE_JPEG_MIME_TYPE_1,
	SOPHOS_IMAGE_JPEG_MIME_TYPE_2,
	SOPHOS_IMAGE_JPEG_MIME_TYPE_3,
	SOPHOS_IMAGE_WEBP_MIME_TYPE,
	SOPHOS_AUDIO_MPEG_MIME_TYPE,
	SOPHOS_AUDIO_MP3_MIME_TYPE,
	SOPHOS_AUDIO_MP4_MIME_TYPE_1,
	SOPHOS_AUDIO_MP4_MIME_TYPE_2,
	SOPHOS_AUDIO_M4A_MIME_TYPE,
	SOPHOS_VIDEO_MP4_MIME_TYPE,
] as const;
export type SophosContentMimeType = (typeof SOPHOS_CONTENT_MIME_TYPES)[number];

// ──────────────────────────────────────────────────────────────────────────────
// Type guards
// ──────────────────────────────────────────────────────────────────────────────

const isInTuple = <T extends readonly string[]>(tuple: T, value: string): value is T[number] =>
	(tuple as readonly string[]).includes(value);

export const isPdfFile = (mimeType: string): mimeType is typeof PDF_MIME_TYPE =>
	mimeType === PDF_MIME_TYPE;

export const isWordFile = (mimeType: string): mimeType is (typeof WORD_MIME_TYPES)[number] =>
	isInTuple(WORD_MIME_TYPES, mimeType);

export const isExcelFile = (mimeType: string): mimeType is (typeof EXCEL_MIME_TYPES)[number] =>
	isInTuple(EXCEL_MIME_TYPES, mimeType);

export const isSheetFile = (mimeType: string): mimeType is (typeof SHEET_MIME_TYPE)[number] =>
	isInTuple(SHEET_MIME_TYPE, mimeType);

export const isCSVFileByTitleOrMimeType = ({
	mimeType,
	title,
}: {
	mimeType?: string;
	title?: string;
}) => mimeType?.toLowerCase().includes("csv") || title?.toLowerCase().endsWith(".csv");

export const isPowerPointFile = (
	mimeType: string,
): mimeType is (typeof POWERPOINT_MIME_TYPES)[number] => isInTuple(POWERPOINT_MIME_TYPES, mimeType);

export const isOfficeFile = (mimeType: string): mimeType is (typeof OFFICE_MIME_TYPES)[number] =>
	isInTuple(OFFICE_MIME_TYPES, mimeType);

export const isImageFile = (mimeType: string): mimeType is (typeof IMAGE_MIME_TYPES)[number] =>
	isInTuple(IMAGE_MIME_TYPES, mimeType);

export const isAudioFile = (mimeType: string): mimeType is (typeof AUDIO_MIME_TYPES)[number] =>
	isInTuple(AUDIO_MIME_TYPES, mimeType);

export const isVideoFile = (mimeType: string): mimeType is (typeof VIDEO_MIME_TYPES)[number] =>
	isInTuple(VIDEO_MIME_TYPES, mimeType);

export const isTextFile = (mimeType: string): mimeType is (typeof TEXT_MIME_TYPES)[number] =>
	isInTuple(TEXT_MIME_TYPES, mimeType);

// ──────────────────────────────────────────────────────────────────────────────
// Extension mapping
// ──────────────────────────────────────────────────────────────────────────────

const MIME_TYPE_TO_EXTENSION = new Map<string, string>([
	[TEXT_CSV_MIME_TYPE, "csv"],
	[EXCEL_XLS_MIME_TYPE, "xls"],
	[EXCEL_XLSX_MIME_TYPE, "xlsx"],
	[TEXT_PLAIN_MIME_TYPE, "txt"],
	[TEXT_HTML_MIME_TYPE, "html"],
	[TEXT_MARKDOWN_MIME_TYPE, "md"],
	[PDF_MIME_TYPE, "pdf"],
	[WORD_DOC_MIME_TYPE, "doc"],
	[WORD_DOCX_MIME_TYPE, "docx"],
	[POWERPOINT_PPT_MIME_TYPE, "ppt"],
	[POWERPOINT_PPTX_MIME_TYPE, "pptx"],
	[JSON_MIME_TYPE, "json"],
]);

export const getExtensionForMimeType = (mimeType: string): string | undefined =>
	MIME_TYPE_TO_EXTENSION.get(mimeType.toLowerCase());

// ──────────────────────────────────────────────────────────────────────────────
// Document template MIME types
// ──────────────────────────────────────────────────────────────────────────────

export const DOCUMENT_TEMPLATE_MIME_TYPES = [
	POWERPOINT_PPTX_MIME_TYPE,
	WORD_DOCX_MIME_TYPE,
] as const;

export type DocumentTemplateMimeType = (typeof DOCUMENT_TEMPLATE_MIME_TYPES)[number];

export type TaggableMimeType =
	| ContentMimeType
	| "video"
	| "folder"
	| "link"
	| "file"
	| "drive"
	| "workspace";

export const TOOL_TYPE_BY_MIME_TYPE = {
	block: "parent-block",
	linkup: "web",
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"code-based-table-analysis": "excel",
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"deep-diff": "diff",
	mailbox: "mailbox",
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"excel-workbook": "excel",
} as const satisfies Record<ToolMimeType, string>;

export const DOCUMENT_TYPE_BY_MIME_TYPE = {
	[TEXT_HTML_MIME_TYPE]: "html",
	[TEXT_PLAIN_MIME_TYPE]: "file",
	[TEXT_MARKDOWN_MIME_TYPE]: "markdown",
	[TEXT_CSV_MIME_TYPE]: "csv",
	[PDF_MIME_TYPE]: "pdf",
	[WORD_DOC_MIME_TYPE]: "word",
	[OUTLOOK_MSG_MIME_TYPE]: "msg",
	[WORD_DOCX_MIME_TYPE]: "word",
	[EXCEL_XLS_MIME_TYPE]: "excel",
	[EXCEL_XLSX_MIME_TYPE]: "excel",
	[POWERPOINT_PPT_MIME_TYPE]: "powerpoint",
	[POWERPOINT_PPTX_MIME_TYPE]: "powerpoint",
	video: "youtube",
	folder: "folder",
	drive: "drive",
	workspace: "workspace",
	file: "file",
	link: "link",
	[AUDIO_MPEG_MIME_TYPE]: "audio",
	[AUDIO_MP3_MIME_TYPE]: "audio",
	[AUDIO_MP4_MIME_TYPE]: "audio",
	[VIDEO_MP4_MIME_TYPE]: "audio",
	[AUDIO_M4A_MIME_TYPE]: "audio",
	[IMAGE_JPEG_MIME_TYPE]: "image",
	[IMAGE_PNG_MIME_TYPE]: "image",
	[IMAGE_WEBP_MIME_TYPE]: "image",
} as const satisfies Record<TaggableMimeType, string>;

export type DocumentType = (typeof DOCUMENT_TYPE_BY_MIME_TYPE)[TaggableMimeType];
